import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

client.interceptors.request.use((cfg) => {
  try { const t = localStorage.getItem("icdh_token"); if (t) { cfg.headers = cfg.headers || {}; cfg.headers["Authorization"] = `Bearer ${t}`; } } catch (e) { /* noop */ }
  try { const id = sessionStorage.getItem("impersonate_id"); if (id) { cfg.headers = cfg.headers || {}; cfg.headers["X-Impersonate-Id"] = id; } } catch (e) { /* noop */ }
  try { const portal = sessionStorage.getItem("active_portal"); if (portal) { cfg.headers = cfg.headers || {}; cfg.headers["X-Portal"] = portal; } } catch (e) { /* noop */ }
  return cfg;
});

// Persist the auth token returned by login/session responses (Bearer fallback for
// browsers that drop the cross-site auth cookie on POST), and clear it on logout.
client.interceptors.response.use((res) => {
  try {
    const u = res.config?.url || "";
    if (res.data && typeof res.data === "object" && res.data.token && (u.includes("/auth/login") || u.includes("/auth/session") || u.includes("/auth/register") || u.includes("/auth/student-login"))) {
      localStorage.setItem("icdh_token", res.data.token);
    }
    if (u.includes("/auth/logout")) localStorage.removeItem("icdh_token");
  } catch (e) { /* noop */ }
  return res;
});

const ERR_FR = {
  "Something went wrong. Please try again.": "Une erreur est survenue. Veuillez réessayer.",
  "Currency must be USD or HTG": "La devise doit être USD ou HTG",
  "Email already in use": "Cet email est déjà utilisé",
  "Email already registered": "Cet email est déjà enregistré",
  "Employee not found": "Employé introuvable",
  "Exam not found": "Examen introuvable",
  "Expense not found": "Dépense introuvable",
  "File not found": "Fichier introuvable",
  "Forbidden": "Accès refusé",
  "Insufficient permissions": "Permissions insuffisantes",
  "Invalid email or password": "Email ou mot de passe invalide",
  "Invalid or used reset link": "Lien de réinitialisation invalide ou déjà utilisé",
  "Invalid session": "Session invalide",
  "Invalid student code or PIN": "Code élève ou PIN invalide",
  "Invoice not found": "Facture introuvable",
  "Master admin only": "Réservé à l'administrateur principal",
  "Not authenticated": "Non authentifié",
  "Not found": "Introuvable",
  "Nothing to update": "Rien à mettre à jour",
  "Only admin can download report cards": "Seul l'administrateur peut télécharger les bulletins",
  "Only parents can register children": "Seuls les parents peuvent inscrire des enfants",
  "Payment not found": "Paiement introuvable",
  "Payroll record not found": "Fiche de paie introuvable",
  "Report card not yet published": "Bulletin pas encore publié",
  "Report card locked: please settle outstanding fees at the économat.": "Bulletin bloqué : veuillez régulariser vos frais à l'économat.",
  "Exam access blocked: fees not settled. Please contact the admin for an override.": "Accès à l'examen bloqué : frais non réglés. Contactez l'administration pour une dérogation.",
  "Reset link has expired": "Le lien de réinitialisation a expiré",
  "Student must be accepted first": "L'élève doit d'abord être accepté",
  "Student not active": "Élève non actif",
  "Student not found": "Élève introuvable",
  "Students only": "Réservé aux élèves",
  "Too many failed attempts. Please try again later.": "Trop de tentatives échouées. Veuillez réessayer plus tard.",
  "Unauthorized": "Non autorisé",
  "You have already taken this exam": "Vous avez déjà passé cet examen",
};

function localize(msg) {
  try {
    if (localStorage.getItem("icdh_lang") === "fr" && ERR_FR[msg]) return ERR_FR[msg];
  } catch (e) { /* noop */ }
  return msg;
}

export function apiErr(detail) {
  if (detail == null) return localize("Something went wrong. Please try again.");
  if (typeof detail === "string") return localize(detail);
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---- Presenter (demo) masking: hide real family names in GET responses ----
let PRESENTER_MASK = false;
export function setPresenterMask(on) { PRESENTER_MASK = !!on; }

const _maskLast = (last) => {
  const t = String(last).trim();
  return t ? t[0].toUpperCase() + "." : t;
};
const _maskFull = (name) => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return name;
  const last = parts.pop();
  return `${parts.join(" ")} ${_maskLast(last)}`;
};
const _NAME_KEYS = ["student_name", "teacher_name", "parent_name", "child_name", "guardian_name", "full_name", "father_name", "mother_name", "author_name", "employee_name", "candidate_name"];
function maskPersonData(d) {
  if (Array.isArray(d)) { for (let i = 0; i < d.length; i++) d[i] = maskPersonData(d[i]); return d; }
  if (d && typeof d === "object") {
    // Fictional demo records (is_demo) are safe to show in full — only real data is masked.
    if (d.is_demo === true) {
      for (const k in d) { if (d[k] && typeof d[k] === "object") d[k] = maskPersonData(d[k]); }
      return d;
    }
    for (const k in d) { if (d[k] && typeof d[k] === "object") d[k] = maskPersonData(d[k]); }
    let isPerson = false;
    if (typeof d.last_name === "string" && d.last_name.trim()) {
      d.last_name = _maskLast(d.last_name);
      if (typeof d.first_name === "string") d.name = `${d.first_name} ${d.last_name}`.trim();
      isPerson = true;
    } else if (typeof d.first_name === "string" && typeof d.name === "string") {
      d.name = _maskFull(d.name);
      isPerson = true;
    } else if (typeof d.name === "string" && d.name.trim() && (typeof d.email === "string" || typeof d.phone === "string")) {
      d.name = _maskFull(d.name);
      isPerson = true;
    }
    if (isPerson) {
      if (typeof d.email === "string") d.email = null;
      if (typeof d.phone === "string") d.phone = null;
    }
    for (const nk of _NAME_KEYS) {
      if (typeof d[nk] === "string" && d[nk].trim()) d[nk] = _maskFull(d[nk]);
    }
    return d;
  }
  return d;
}

export const api = {
  get: (u, cfg) => client.get(u, cfg).then((r) => (PRESENTER_MASK ? maskPersonData(r.data) : r.data)),
  post: (u, d, cfg) => client.post(u, d, cfg).then((r) => r.data),
  put: (u, d, cfg) => client.put(u, d, cfg).then((r) => r.data),
  patch: (u, d) => client.patch(u, d).then((r) => r.data),
  del: (u) => client.delete(u).then((r) => r.data),
  raw: client,
};
